import React, { useMemo, useCallback } from 'react';
import { StyleSheet, Pressable, Image, ActivityIndicator, View, ImageBackground, Platform } from 'react-native';
import { Image as ExpoImage } from 'expo-image';
import checkerLight from '@/assets/images/checker-light.png';
import checkerDark from '@/assets/images/checker-dark.png';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { canUseDesktopEditors, openMessageFile, formatFileSize, getFileIconByMimeType, getFileTypeLabel, getFileTypeColor } from '@/constants/utils/files';
import { imageFileUrl, isImageArtifact } from '@/lib/imageArtifact';
import { codeChangeStats } from '@/constants/utils/code_diff';
import MediaImagePreview from '@/components/Media/MediaImagePreview';
import { ALLOWED_INPAINTING_FILE_TYPES } from '@/constants/config';
import { isMediaOutputFile } from '@/constants/utils/message';
import { useVideoPlayer, VideoView } from 'expo-video';
import useTranslation from '@/hooks/useTranslation';

const createStyles = (colors, isSmall, isIconOnly) => StyleSheet.create({
  container: {
    position: 'relative',
    maxWidth: 
      isIconOnly ? 
        (isSmall ? 42 : 56) : isSmall ? 280 : 320,
  },
  mediaContainer: {
    width: isSmall ? 42 : 140,
    height: isSmall ? 42 : 140,
    backgroundColor: colors.solid_color_block,
    borderRadius: isSmall ? 8 : 12,
  },
  mediaContent: {
    width: '100%',
    height: '100%',
    borderRadius: isSmall ? 8 : 12,
    overflow: 'hidden',
  },
  documentContainer: {
    height: isSmall ? 42 : 56,
    paddingLeft: isIconOnly ? 2 : isSmall ? 4 : 6,
    paddingRight: isIconOnly ? 2 : isSmall ? 10 : 12,
    backgroundColor: colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    flexDirection: 'row',
    alignItems: 'center',
    gap: isSmall ? 6 : 8,
    // flex: 0, // Don't grow
    flexShrink: 1, // Allow shrinking
    minWidth: 
      isIconOnly ? 
        (isSmall ? 42 : 56) : 200,
    borderRadius: isSmall ? 8 : 12,
  },
  fileContent: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.solid_color_block,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  checkerboard: {
    position: 'absolute',
    opacity: 0.5,
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: isSmall ? 8 : 12,
  },
  checkerImage: {
    resizeMode: 'repeat',
    height: '100%',
    width: '100%',
  },
  deleteButton: {
    position: 'absolute',
    top: -6,
    right: -6,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.solid_color_primary,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  fileIconContainer: {
    width: isSmall ? 36 : 46,
    height: isSmall ? 36 : 46,
    borderRadius: isSmall ? 8 : 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileTextContainer: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
    minWidth: 0,
  },
  fileName: {
    color: colors.text_color_primary,
    fontSize: 14,
    minWidth: 0,
    maxWidth: '100%',
    ...(Platform.OS === 'web' ? {
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    } : {}),
  },
  fileType: {
    display: 'flex',
    flexShrink: 0,
    color: colors.text_color_secondary,
    fontSize: 12,
    marginTop: isSmall ? 1 : 2,
    minWidth: 40,
  },
  fileDot: {
    display: 'flex',
    flexShrink: 0,
    color: colors.text_color_secondary,
    fontSize: 12,
    marginTop: isSmall ? 1 : 2,
  },
  centerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: isSmall ? 8 : 12,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: isSmall ? 8 : 12,
  },
});

const MediaCard = ({ file, onDelete, onPress, isSmall, isEditable = false, onEdit, isIconOnly = false, mediaSize }) => {
  const { t } = useTranslation();
  const { colors, theme } = useTheme();
  const styles = useMemo(() => createStyles(colors, isSmall, isIconOnly), [colors, isSmall, isIconOnly]);
  const fileIcon = getFileIconByMimeType(file?.mimeType || file?.type || file?.fileType || file?.extension);
  const isVideo = file?.fileType === "video" || file?.type?.startsWith('video/') || isMediaOutputFile(file, 'video');
  const isImage = !isVideo && (isImageArtifact(file) || file?.type?.startsWith('image/'));
  const is3D = file?.fileType === '3d' || isMediaOutputFile(file, '3d');
  const isMedia = isImage || isVideo || is3D;
  const [isHovered, setIsHovered] = React.useState(false);
  const [videoThumbFailed, setVideoThumbFailed] = React.useState(false);
  const fileTypeColor = getFileTypeColor(file?.mimeType || file?.type || file?.fileType || file?.extension);
  const fileTypeLabel = getFileTypeLabel(file?.type);
  const changeStats = codeChangeStats(file);

  // 
  // turn off for now
  // 
  isEditable = false;
  // ------------------

  const canEdit = useMemo(() => {
    if (!isEditable) return false;
    const type = file?.fileType || file?.type;
    // Check if type matches or starts with any allowed type
    return ALLOWED_INPAINTING_FILE_TYPES.some(t => type === t || type?.startsWith?.(t + '/'));
  }, [isEditable, file]);

  const imageUrl = useMemo(() => {
    if (file?.thumbnailPath) {
      return file?.thumbnailPath;
    }
    if (file?.cdnPath) {
      return file.cdnPath;
    }
    if (file?.url) {
      return file?.url;
    }
    // On native platforms, use the uri directly from the file object
    if (Platform.OS !== 'web' && file?.uri) {
      return file.uri;
    }
    // On web, use URL.createObjectURL for File objects
    if (Platform.OS === 'web' && isImage && (file instanceof File || file?.previewFile instanceof File)) {
      const fileToPreview = file instanceof File ? file : file?.previewFile;
      return URL.createObjectURL(fileToPreview);
    }
    return null;
  }, [file, isImage]);

  const videoSourceUrl = useMemo(() => {
    if (!isVideo) return null;
    if (file?.cdnPath) return file.cdnPath;
    if (file?.url) return file.url;
    return null;
  }, [file, isVideo]);

  const videoThumbnailUrl = useMemo(() => {
    if (!isVideo) return null;
    if (file?.thumbnailPath) return file.thumbnailPath;
    return null;
  }, [file, isVideo]);

  React.useEffect(() => {
    setVideoThumbFailed(false);
  }, [videoThumbnailUrl, file?.id, file?.cdnPath]);

  const showVideoThumb = !!(videoThumbnailUrl && !videoThumbFailed);

  const player = useVideoPlayer(isVideo && !showVideoThumb ? (videoSourceUrl ?? null) : null, (p) => {
    if (!isVideo) return;
    p.loop = false;
    p.muted = false; // Explicitly enable audio playback on web
    p.pause();
  });

  React.useEffect(() => {
    return () => {
      if (imageUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const opensImageStudio = !onPress && isImage && !file?.isUploading && !!imageFileUrl(file) && canUseDesktopEditors();
  const handlePress = useCallback(() => {
    if (onPress) {
      onPress(file);
    } else if (opensImageStudio) {
      openMessageFile(file);
    }
  }, [file, onPress, opensImageStudio]);

  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit(file);
    }
  }, [onEdit, file]);

  const renderLoadingOverlay = () => {
    if (!file.isUploading) return null;
    return (
      <View style={styles.loadingOverlay}>
        <ActivityIndicator size="small" color={colors.text_color_alt} />
      </View>
    );
  };

  const Container = onPress ? Pressable : View;

  if (isMedia) {
    return (
      <View
        style={[styles.container, styles.mediaContainer, mediaSize && { width: mediaSize, height: mediaSize }]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View style={styles.mediaContent}>
          <View style={styles.fileContent}>
            {isImage && !file?.blurhash && (
              <ImageBackground
                source={isDarkTheme(theme) ? checkerDark : checkerLight}
                style={styles.checkerboard}
                imageStyle={styles.checkerImage}
                resizeMode="repeat"
              />
            )}
            {isImage && imageUrl ? (
              <MediaImagePreview
                uri={imageUrl}
                file={file}
                style={styles.imagePreview}
                contentFit="cover"
                placeholder={file?.blurhash ? { blurhash: file?.blurhash } : undefined}
                transition={200}
                preferThumbnail
              />
            ) : is3D ? (
              <Icon name="cube" size={isSmall ? 18 : 32} color={colors.text_color_secondary} />
            ) : isVideo ? (
              showVideoThumb ? (
                <>
                  <Image
                    source={{ uri: videoThumbnailUrl }}
                    style={styles.imagePreview}
                    onError={() => setVideoThumbFailed(true)}
                  />
                  <View style={styles.centerOverlay}>
                    <Icon name="play-circle" size={isSmall ? 18 : 32} color="#fff" />
                  </View>
                </>
              ) : videoSourceUrl ? (
                <>
                  <VideoView
                    player={player}
                    style={styles.imagePreview}
                    nativeControls={false}
                    allowsFullscreen={false}
                    allowsPictureInPicture={false}
                    contentFit="cover"
                    crossOrigin="anonymous"
                  />
                  <View style={styles.centerOverlay}>
                    <Icon name="play-circle" size={isSmall ? 18 : 32} color="#fff" />
                  </View>
                </>
              ) : (
                <View style={[styles.imagePreview, { backgroundColor: '#14181c', alignItems: 'center', justifyContent: 'center' }]}>
                  <Icon name="play-circle" size={isSmall ? 18 : 32} color="#fff" />
                </View>
              )
            ) : (
              <Icon name="image" size={isSmall ? 18 : 32} color={colors.text_color_secondary} />
            )}
          </View>
        </View>
        {renderLoadingOverlay()}
        {(onPress || opensImageStudio) && (
          <Pressable
            style={[StyleSheet.absoluteFillObject, { borderRadius: isSmall ? 8 : 12, zIndex: 1 }]}
            onPress={handlePress}
            accessibilityRole="button"
            accessibilityLabel={opensImageStudio ? `Edit ${file?.name || 'image'} in Image studio` : `Open ${file?.name || 'file'}`}
            {...(Platform.OS === 'web' && opensImageStudio ? { title: 'Edit in Image studio' } : {})}
          >
            {opensImageStudio && !isSmall && (
              <View pointerEvents="none" style={{ position: 'absolute', right: 7, bottom: 7, flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 3, paddingHorizontal: 6, borderRadius: 6, backgroundColor: 'rgba(0, 0, 0, 0.7)' }}>
                <Icon name="wand-magic-sparkles" size={11} color="#fff" />
                <Text style={{ color: '#fff', fontSize: 11 }}>Edit</Text>
              </View>
            )}
          </Pressable>
        )}
        {onDelete && (
          <Pressable style={styles.deleteButton} onPress={() => onDelete(file)} accessibilityRole="button" accessibilityLabel={`Remove ${file?.name || 'file'} attachment`}>
            <Icon name="xmark" size={10} color={colors.text_color_primary} />
          </Pressable>
        )}
        {canEdit && (
          <Pressable 
            style={[styles.deleteButton, onDelete ? { right: 18 } : null]}
            onPress={handleEdit}
          >
            <Icon name="pen-to-square" size={10} color={colors.text_color_primary} />
          </Pressable>
        )}
      </View>
    );
  }

  return (
    <Container 
      style={[styles.container, styles.documentContainer]}
      onPress={handlePress}
    >
      <View style={[styles.fileIconContainer, { backgroundColor: fileTypeColor + '20' }]}> 
        <Icon name={fileIcon} size={isSmall ? 18 : 24} color={fileTypeColor} />
      </View>

      {!isIconOnly && (
        <View style={styles.fileTextContainer}>
          <Text style={styles.fileName} numberOfLines={1} ellipsizeMode="tail">
            {file?.name || ''}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <Text style={styles.fileType} size={isSmall ? 's' : 'n'}>{fileTypeLabel}</Text>
            <Text style={styles.fileDot} size={isSmall ? 's' : 'n'}>•</Text>
            <Text style={styles.fileType} size={isSmall ? 's' : 'n'}>{formatFileSize(file?.sizeInBytes ?? file?.size)}</Text>
            {changeStats ? (
              <>
                <Text style={styles.fileDot} size={isSmall ? 's' : 'n'}>•</Text>
                <Text style={[styles.fileType, { color: '#22C55E' }]} size={isSmall ? 's' : 'n'}>+{changeStats.added}</Text>
                <Text style={[styles.fileType, { color: '#EF4444' }]} size={isSmall ? 's' : 'n'}>−{changeStats.removed}</Text>
              </>
            ) : null}
            {file?.isUploading && (
              <>
                <Text style={styles.fileDot} size={isSmall ? 's' : 'n'}>•</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <ActivityIndicator size="small" color={colors.text_color_secondary} />
                </View>
              </>
            )}
            {!file?.isUploading && file?.processingStatus?.status === 'pending' && (
              <>
                <Text style={styles.fileDot} size={isSmall ? 's' : 'n'}>•</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <ActivityIndicator size="small" color={colors.text_color_secondary} />
                  <Text style={styles.fileType} size={isSmall ? 's' : 'n'}>{t('chatInput.files.analyzing')}</Text>
                </View>
              </>
            )}
            {!file?.isUploading && file?.processingStatus?.status === 'processing' && (
              <>
                <Text style={styles.fileDot} size={isSmall ? 's' : 'n'}>•</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <ActivityIndicator size="small" color={colors.text_color_secondary} />
                  <Text style={styles.fileType} size={isSmall ? 's' : 'n'}>{t('chatInput.files.processing')}</Text>
                </View>
              </>
            )}
            {!file?.isUploading && file?.processingStatus?.status === 'failed' && (
              <>
                <Text style={styles.fileDot} size={isSmall ? 's' : 'n'}>•</Text>
                <Text style={[styles.fileType, { color: colors.error_color }]} size={isSmall ? 's' : 'n'}>{t(file.processingStatus.statusCheckTimedOut ? 'chatInput.files.statusUnknown' : 'chatInput.files.failed')}</Text>
              </>
            )}
          </View>
        </View>
      )}
      {onDelete && (
        <Pressable style={styles.deleteButton} onPress={() => onDelete(file)} accessibilityRole="button" accessibilityLabel={`Remove ${file?.name || 'file'} attachment`}>
          <Icon name="xmark" size={10} color={colors.text_color_primary} />
        </Pressable>
      )}
      {canEdit && (
        <Pressable 
          style={[styles.deleteButton, onDelete ? { right: 18 } : null]}
          onPress={handleEdit}
        >
          <Icon name="pen-to-square" size={10} color={colors.text_color_primary} />
        </Pressable>
      )}
    </Container>
  );
};

export default MediaCard;
